import { useEffect } from 'react';

import { persistCart } from '../../../lib/cartStorage';
import { normalizeCartLines } from '../utils/cartNormalization';

type CartItem = {
  id?: string;
  productId?: string;
  quantity: number;
  temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either';
};

type UseCartPersistenceArgs = {
  cart: CartItem[];
  currentUserId?: string | null;
  syncCartToServer: (items: CartItem[], options?: { keepalive?: boolean }) => void;
};

const CHECKOUT_SYNC_SUPPRESS_KEY = 'ninpo_suppress_cart_sync';

/** Call before Stripe redirect so pagehide cannot resurrect the cart. */
export function suppressCartServerSync(ms = 5 * 60 * 1000): void {
  try {
    sessionStorage.setItem(CHECKOUT_SYNC_SUPPRESS_KEY, String(Date.now() + ms));
  } catch {
    // ignore
  }
}

function isCartServerSyncSuppressed(): boolean {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_SYNC_SUPPRESS_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until) || Date.now() > until) {
      sessionStorage.removeItem(CHECKOUT_SYNC_SUPPRESS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function useCartPersistence({ cart, currentUserId, syncCartToServer }: UseCartPersistenceArgs) {
  useEffect(() => {
    const normalized = normalizeCartLines(cart);
    persistCart(normalized);
    const timeout = setTimeout(() => {
      if (currentUserId && !isCartServerSyncSuppressed()) {
        void Promise.resolve(syncCartToServer(normalized)).catch(() => undefined);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [cart, currentUserId, syncCartToServer]);

  useEffect(() => {
    const flush = () => {
      const normalized = normalizeCartLines(cart);
      persistCart(normalized);
      if (currentUserId && !isCartServerSyncSuppressed()) {
        void Promise.resolve(syncCartToServer(normalized, { keepalive: true })).catch(() => undefined);
      }
    };
    window.addEventListener('pagehide', flush);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [cart, currentUserId, syncCartToServer]);
}
